import type { FunctionComponent } from 'react';

import { CommentableTitle, useTitleCommentRow, useTitleThreadState } from '../../../../comments';

/** The task title's type, which a page may borrow so a title it shows matches this one. */
export const TASK_TITLE_TYPE_CLASS_NAME = 'text-h3 font-medium leading-tight tracking-tight';

const HEADING_CLASS_NAME = `mb-0 mt-0 ${TASK_TITLE_TYPE_CLASS_NAME} text-text-primary [overflow-wrap:anywhere]`;

/** The task title as plain text, for readers who cannot comment. */
export const TaskHeading: FunctionComponent<{ readonly title: string }> = ({ title }) => (
    <h2 className={HEADING_CLASS_NAME}>{title}</h2>
);

/**
 * The task title as the comment rail's title anchor row. Mount inside a `CommentableTitle`; the
 * profile-task and work-task tiles share it so a title thread resolves against either of them.
 */
export const CommentableTaskHeading: FunctionComponent<{ readonly title: string }> = ({ title }) => {
    const { onRowClick, rowDataAttrs, titleInputRef } = useTitleCommentRow();
    const { highlightClassName } = useTitleThreadState();

    return (
        <h2
            className={HEADING_CLASS_NAME}
            onClick={onRowClick}
            onKeyDown={
                onRowClick === undefined
                    ? undefined
                    : (event) => {
                          if (event.key !== 'Enter' && event.key !== ' ') return;
                          event.preventDefault();
                          onRowClick();
                      }
            }
            role={onRowClick === undefined ? undefined : 'button'}
            tabIndex={onRowClick === undefined ? undefined : 0}
            {...rowDataAttrs}
        >
            <span ref={titleInputRef} className={highlightClassName}>
                {title}
            </span>
        </h2>
    );
};

/** The task detail tile's head: its title, which takes comments where they can be written. Only Home shows task cassettes. */
export const TaskDetailHeading: FunctionComponent<{
    /** The comment item the title anchors to; without it the title is plain text. */
    readonly commentItemId: string | undefined;
    readonly title: string;
}> = ({ commentItemId, title }) => (
    <div className="flex flex-col gap-3xl">
        {commentItemId === undefined ? (
            <TaskHeading title={title} />
        ) : (
            <CommentableTitle itemId={commentItemId} selectionCommentEnabled={true} title={title}>
                <CommentableTaskHeading title={title} />
            </CommentableTitle>
        )}
    </div>
);
